import {Vue} from "vue-property-decorator";
import {CheckoutFormData, PaymentOptions, Sex} from "@/types/CheckoutFormData";
import CheckoutServerData, {
    CheckoutData,
    Country,
    Option,
    ProductButton,
    ProductCategory
} from "@/types/CheckoutServerData";
import axios from "@/plugins/axios";
import {Mixin} from "@/mixins";
import {SectionType, ShippingType, SyncServerSource} from "@/types/enums";
import * as Sentry from "@sentry/vue";
import {captureException, withScope} from "@sentry/browser";

const mixins = new Mixin();

export class CheckoutStore {
    constructor(personalizedCartId: string | null = null) {
        // nice loading
        let productCount = 0;
        let headerCartEl = document.querySelector('.cart .cart-img .number');
        if (headerCartEl) {
            productCount = Number(headerCartEl.innerHTML);
        }
        const category: ProductCategory = {
            name: '',
            isDefault: true,
            products: {},
        };
        for (let i = 0; i < productCount; i++) {
            category.products[i] = {
                id: i,
                name: '------------ ------',
                description: '---------- ------ ---------- ------ -------- ------- ----- ------- ---- ---------- ------',
                price: '---,-- €',
                buttons: [ProductButton.Edit, ProductButton.Copy, ProductButton.Delete],
                delivery: {
                    from: 7,
                    to: 10
                },
                specialWishEnabled: true,
                type: 'placeholder',
                classification: '',
                bulletpoints: [],
                generateImage: false,
            };
        }
        this.serverData.products[0] = category;

        try {
            let oldFormDataJson = window.sessionStorage.getItem('ch2-formData');
            if (oldFormDataJson !== null) {
                let oldFormData = JSON.parse(oldFormDataJson);
                if (typeof oldFormData === 'object') {
                    Object.assign(this.state.formData, oldFormData);
                }
            }
        } catch (ex) {
            // do nothing
        }

        this.state.isPreview = window.location.search.indexOf('preview=true') > -1;
        this.personalizedCartId = personalizedCartId;

        document.addEventListener('activatedHeaderBarVoucherPopup', () => {
            this.activateHeaderBarVoucher();
        });
    }

    private state: {
        hasInitialData: boolean,
        serverData: CheckoutData, // data incoming from backend
        formData: CheckoutFormData,
        loading: boolean,
        firstServerSync: boolean,
        showErrorSections: Array<SectionType>,
        showAssemblyZipError: boolean,
        assemblyConfirmed: boolean,
        currentSection: SectionType,
        hasErrorOnBuy: boolean,
        personalizedCartId: string | null,
        isPreview: boolean,
        availablePaymentOptions: { [key: number]: Option },
        availableShippingOptions: { [key: number]: Option },
        countries: { [key: string]: Country } | null,
    } = new Vue({
        watch: {
            formData: {
                handler(val) {
                    let valCopy: CheckoutFormData = JSON.parse(JSON.stringify(val));
                    delete valCopy.creditCard;
                    window.sessionStorage.setItem('ch2-formData', JSON.stringify(valCopy));
                    Sentry.getCurrentHub().configureScope((scope) => scope.setExtra('form-data', valCopy));
                },
                deep: true,
            },
            'serverData.billingAddress.country'(val: string, old: string) {
                if (
                    this.serverData.billingAddress.phone === ''
                    || (
                        this.countries !== null
                        && typeof this.countries[old] !== 'undefined'
                        && this.serverData.billingAddress.phone === this.countries[old].phoneCode
                    )
                ) {
                    const countryData = this.countries[val];
                    this.serverData.billingAddress.phone = countryData ? countryData.phoneCode : '';
                }
            },
            'serverData.billingAddress': {
                handler() {
                    this.serverData.addressChanged = true;
                },
                deep: true,
            },
            'serverData.deliveryAddress': {
                handler() {
                    this.serverData.addressChanged = true;
                },
                deep: true,
            },
            'serverData.totalPriceNumber'(val: number) {
                if (this.serverData.totalPriceNumber <= 0) {
                    //this.formData.payment = PaymentOptions.Empty; @TODO we need payment
                }
            }
        },
        data: () => {
            return {
                hasInitialData: false,
                loading: true,
                firstServerSync: true,
                showAssemblyZipError: false,
                assemblyConfirmed: false,
                showErrorSections: [],
                currentSection: SectionType.Address,
                hasErrorOnBuy: false,
                personalizedCartId: null,
                isPreview: false,
                formData: {
                    creditCard: {
                        number: '',
                        expiration: {
                            month: '01',
                            year: '21'
                        },
                        name: '',
                        cvc: ''
                    },
                },
                serverData: {
                    customer: {
                        hasDifferentBillingAddress: false,
                        billingAddress: {
                            sex: Sex.Female,
                            name: '',
                            surname: '',
                            hasCompany: false,
                            company: '',
                            vatNumber: '',
                            street: '',
                            zipcode: '',
                            city: '',
                            country: this.defaultCountry,
                            phone: '',
                            email: '',
                        },
                        deliveryAddress: {
                            sex: Sex.Male,
                            name: '',
                            surname: '',
                            addition: '',
                            street: '',
                            zipcode: '',
                            city: '',
                            country: this.defaultCountry,
                        },
                        addressChanged: false,
                    },
                    additionalOptions: {
                        assembly: false,
                        hasVoucher: false,
                        voucher: {
                            code: '',
                        },
                        trustedShops: false,
                        hasSpecialWish: false,
                        specialWish: ''
                    },
                    productsSpecialWish: {},
                    shipping: null,
                    payment: PaymentOptions.Empty,
                    newsletter: null,
                    agb: false,
                    invoice: {
                        birthDate: {
                            day: null,
                            month: null,
                            year: null
                        }
                    },
                    products: {},
                    productCount: 0,
                    subtotal: '----.-- €',
                    subtotalNumber: 0,
                    shippingOptions: {
                        first: {
                            label: 'Lieferun per Spedition',
                            price: '0,00 €',
                            default: true
                        },
                        second: {
                            label: 'Lieferun per Spedition',
                            sublabel: 'Samstagzustellung für 39€ von 8-16h',
                            price: '39,00 €'
                        },
                        third: {
                            label: 'Lieferun per Spedition',
                            sublabel: 'Feierabenzustellung für 39€ von 17-21h',
                            price: '39,00 €'
                        },
                        fourth: {
                            label: 'Lieferun per Poketdienst',
                            price: '0,00 €'
                        },
                    },
                    paymentOptions: undefined,
                    totalPriceNumber: 0,
                    exchangeRate: 1,
                    totalPrice: '----,-- €',
                    errors: {},
                    warnings: {},
                    redirect: true,
                    configurableProductsPrice: 0,
                    configurableProductsPriceNetto: 0,
                    stepsInfo: {
                        address: '',
                        shipping: {
                            price: '',
                            freeDelivery: false,
                        },
                        payment: '',
                    },
                    token: '',
                    links: {
                        emptyCartReturn: '',
                    },
                },
                availablePaymentOptions: {},
                availableShippingOptions: {},
                countries: {},
            }
        }
    });

    get defaultCountry() {
        // todo get default country from server data
        return window.defaultCheckoutCountry ? window.defaultCheckoutCountry : 1; //Country.Germany;
    }

    get hasInitializedData() {
        return this.state.hasInitialData;
    }

    get serverData() {
        return this.state.serverData;
    }

    get formData() {
        return this.state.formData;
    }

    get flatProducts() {
        return Object.values(this.serverData.products).flatMap(cat => Object.values(cat.products));
    }

    get loading() {
        return this.state.loading;
    }

    set loading(val: boolean) {
        this.state.loading = val;
    }

    get currentSection() {
        return this.state.currentSection;
    }

    set currentSection(val: SectionType) {
        this.state.currentSection = val;
    }

    get showAssemblyZipError() {
        return this.state.showAssemblyZipError;
    }

    set showAssemblyZipError(val: boolean) {
        this.state.showAssemblyZipError = val;
    }

    get assemblyConfirmed() {
        return this.state.assemblyConfirmed;
    }

    set assemblyConfirmed(val: boolean) {
        this.state.assemblyConfirmed = val;
    }

    get hasErrorOnBuy() {
        return this.state.hasErrorOnBuy;
    }

    set hasErrorOnBuy(val: boolean) {
        this.state.hasErrorOnBuy = val;
    }

    get personalizedCartId() {
        return this.state.personalizedCartId;
    }

    set personalizedCartId(val: string | null) {
        this.state.personalizedCartId = val;
    }

    get isPreview() {
        return this.state.isPreview;
    }

    get serverPaymentOptions() {
        return this.state.availablePaymentOptions;
    }

    public removePaymentOptionByIndex(paymentId: number) {
        Vue.delete(this.state.availablePaymentOptions, paymentId);
    }

    get availableCountries() {
        return this.state.countries;
    }

    get availableShippingOptions() {
        return this.state.availableShippingOptions;
    }

    public async checkAssembly(): Promise<boolean> {
        this.showAssemblyZipError = true;

        if (!this.serverData.additionalOptions.assemblyData.canOrder || this.hasError('billingAddress.zipcode', 'errors', true)) {
            return false;
        }

        this.assemblyConfirmed = true;
        return true;
    }

    public async syncServer(actLikeFirstRequest = false, source ?: SyncServerSource) {
        this.state.loading = true;
        this.hasErrorOnBuy = false;
        try {
            let cartData = Object.assign({
                lead_device_type: mixins.getDeviceType(),
                first_request: (this.state.firstServerSync || actLikeFirstRequest),
            }, this.serverData);

            const cartDataUrl = `/cart-data`;
            let url = cartDataUrl;

            if (
                this.personalizedCartId
                && (
                    this.isPreview
                    || (
                        typeof personalizedCartData !== 'undefined'
                        && (personalizedCartData.showModal || personalizedCartData.isExpired)
                    )
                )
            ) {
                url += `/${this.personalizedCartId}`;
            }

            let newServerDataJson;
            try {
                newServerDataJson = await axios.post(url, cartData);
            } catch (e) {
                newServerDataJson = await axios.post(cartDataUrl, cartData);
            }

            Vue.set(this.state, 'serverData', newServerDataJson.data.data);
            Vue.set(this.state, 'availableShippingOptions', newServerDataJson.data.data.shippingOptions);
            Vue.set(this.state, 'availablePaymentOptions', newServerDataJson.data.data.paymentOptions);

            Sentry.getCurrentHub().configureScope((scope) => scope.setExtra('server-data', JSON.stringify(this.state.serverData)));

            if (source !== SyncServerSource.SaveVoucher && this.hasError('additionalOptions.voucher')) {
                this.clearVoucherData();
                this.removeError('additionalOptions.voucher');
            }

            // head cart number
            this.initProductCount();
            // reset address watcher to default
            this.serverData.addressChanged = false;
            // select basic shipping if selected not available
            if (!this.availableShippingOptions[this.serverData.shipping!]) {
                this.serverData.shipping = null;
            }

            // turn off assembly if not available
            if (!this.serverData.additionalOptions.assemblyData) {
                this.serverData.additionalOptions.assembly = false;
            } else if (this.serverData.personalizedCart && this.serverData.personalizedCart.mandatoryAssembly) {
                this.serverData.additionalOptions.assembly = true;
            }

            // if first AJAX, set default phone number
            if (this.state.firstServerSync || actLikeFirstRequest) {
                if (this.serverData.personalizedCart) {
                    if (Array.isArray(this.state.serverData.productsSpecialWish)) {
                        Vue.set(this.state.serverData, 'productsSpecialWish', {});
                    }
                }

                if (
                    this.serverData.billingAddress.phone === '' || this.serverData.billingAddress.phone === null
                    || (
                        typeof this.availableCountries![this.serverData.billingAddress.country] !== 'undefined'
                        && this.serverData.billingAddress.phone === this.availableCountries![this.serverData.billingAddress.country].phoneCode
                    )
                ) {
                    const countryData = this.availableCountries![this.serverData.billingAddress.country];
                    this.serverData.billingAddress.phone = countryData ? countryData.phoneCode : '';
                }

                this.assemblyConfirmed = this.serverData.additionalOptions.assemblyData
                    && !!this.serverData.billingAddress.zipcode
                    && !!this.serverData.additionalOptions.assembly
                    && !!this.serverData.additionalOptions.assemblyData.canOrder
                    && !this.hasError(`billingAddress.zipcode`, 'errors', true);

                await Vue.nextTick();
                document.dispatchEvent(new CustomEvent('firstCartAjax'));
            }

            this.state.firstServerSync = false;
            this.state.loading = false;
        } catch (e) {
            console.error(e);
            this.state.loading = false;
            throw e;
        }
    }

    public setCartData(data: CheckoutServerData) {
        Vue.set(this.state, 'hasInitialData', true);
        Vue.set(this.state, 'serverData', Object.assign({...this.serverData}, data));
        Vue.set(this.state, 'availablePaymentOptions', data.paymentOptions);
        Vue.set(this.state, 'availableShippingOptions', data.shippingOptions);
        this.initProductCount();
        this.state.loading = false;
    }

    public initProductCount(): void {
        let headerCartEl = document.getElementById('cart-item-count');
        if (headerCartEl) {
            headerCartEl.innerHTML = this.serverData.productCount.toString();
        }
    }

    public setCountries(data: { [key: string]: Country }) {
        Vue.set(this.state, 'countries', data);
    }

    public setPaymentOptions(data: { [key: number]: Option }) {
        Vue.set(this.state, 'availablePaymentOptions', data);
    }

    public setShippingOptions(data: { [key in ShippingType]?: Option }) {
        Vue.set(this.state, 'availableShippingOptions', data);
    }

    public async cartData() {
        let url = `/api/cart/data`;

        if (this.hasInitializedData) {
            return;
        }

        if (
            this.personalizedCartId
            && (
                this.isPreview
                || (
                    typeof personalizedCartData !== 'undefined'
                    && (personalizedCartData.showModal || personalizedCartData.isExpired)
                )
            )
        ) {
            url += `/${this.personalizedCartId}`;
        }

        /*try {
            this.state.loading = true;
            // load customer cart data
            const newServerDataJson = await axios.get(url);
            if (typeof newServerDataJson.data !== 'undefined') {
                Vue.set(this.state, 'serverData', Object.assign(this.serverData, newServerDataJson.data.data));
            }
        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
        } finally {
            this.state.loading = false;
        }*/
    }

    public async loadPaymentOptions() {
        this.hasErrorOnBuy = false;
        const newServerDataJson = await axios.get('/api/cart/payments');
        Vue.set(this.state, 'availablePaymentOptions', newServerDataJson.data);
    }

    public async loadCountries() {
        const newServerDataJson = await axios.get(`/api/cart/countries`);
        Vue.set(this.state, 'countries', newServerDataJson.data);
    }

    public async buy() {
        if (this.serverData.personalizedCart && this.serverData.personalizedCart.isExpired) {
            return false;
        }

        if (typeof this.selectedPayment !== 'undefined' && this.selectedPayment!.module === PaymentOptions.CreditCard) {
            this.validateCreditCard();
            if (typeof this.serverData.errors.creditCard !== 'undefined'
                && Object.keys(this.serverData.errors.creditCard).length > 0) {
                this.currentSection = SectionType.Payment;
                this.showErrorsInSection(SectionType.Payment);
                this.hasErrorOnBuy = true;
                return false;
            }
        }
        this.serverData.newsletter = !!this.serverData.newsletter;
        this.hasErrorOnBuy = false;
        this.loading = true;
        try {
            let formData = new FormData();
            let csrfTokenFormData = new FormData();
            csrfTokenFormData.append('d', 'alkjsmc');

            let orderData = Object.assign({
                lead_device_type: mixins.getDeviceType(),
                csrfToken: (await axios.post('/' + lang + '/ajax/csrft', csrfTokenFormData)).data,
            }, this.serverData);

            if (typeof this.selectedPayment !== 'undefined' && this.selectedPayment!.module === PaymentOptions.CreditCard) {
                orderData = Object.assign(orderData, {
                    creditCard: this.formData.creditCard,
                });
            }

            let res = await axios.post('/' + lang + '/finish-order', orderData);
            if (typeof res.data === 'object' && res.data.hasOwnProperty('amazonPay')) {
                res.data.amazonPay.createCheckoutSessionConfig.payloadJSON = mixins.decodeBase64(res.data.amazonPay.createCheckoutSessionConfig.payloadJSON);
                window.amazon.Pay.initCheckout(res.data.amazonPay);
            } else if (typeof res.data === 'object' && res.data.hasOwnProperty('pledg')) {
                window.location.href = res.data.pledg.url + '&' + Date.now();
            } else if (typeof res.data === 'object' && res.data.hasOwnProperty('errors')) {
                Vue.set(this.state.serverData, 'errors', res.data.errors);
                let headerCartEl = document.querySelector('.cart .cart-img .number');
                if (headerCartEl) {
                    headerCartEl!.innerHTML = this.serverData.productCount.toString();
                }
                if (typeof this.serverData.errors.billingAddress !== 'undefined' || typeof this.serverData.errors.deliveryAddress !== 'undefined') {
                    this.currentSection = SectionType.Address;
                } else if (typeof this.serverData.errors.shipping !== 'undefined') {
                    this.currentSection = SectionType.Shipping;
                } else if ((typeof this.serverData.errors.payment !== 'undefined'
                        || typeof this.serverData.errors.invoice !== 'undefined')
                    && this.serverData.totalPriceNumber > 0) {
                    this.currentSection = SectionType.Payment;
                } else if (typeof this.serverData.errors.services !== 'undefined') {
                    this.currentSection = SectionType.Options;
                } else if (typeof this.serverData.errors.agb !== 'undefined') {
                    this.currentSection = SectionType.Options;
                }
                this.hideAllErrors();
                this.showErrorsInSection(SectionType.Address);
                this.showErrorsInSection(SectionType.Shipping);
                this.showErrorsInSection(SectionType.Payment);
                this.showErrorsInSection(SectionType.Options);

                this.loading = false;
            } else if (typeof res.data.url !== 'undefined') {
                window.addEventListener('pagehide', (event) => {
                    this.loading = false;
                });
                document.location.href = res.data.url;
            } else {
                this.loading = false;
            }
            return;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    validateCreditCard() {
        if (!this.shouldShowCreditCardInputs) {
            return;
        }

        if (typeof this.serverData.errors.creditCard === 'undefined') {
            Vue.set(this.serverData.errors, 'creditCard', {});
        }
        var numberValidation = PayEngine.validateCardNumber(this.formData.creditCard.number);
        var cvcValidation = PayEngine.validateVerification(this.formData.creditCard.cvc);
        var expiryValidation = PayEngine.validateExpiry(this.formData.creditCard.expiration.year, this.formData.creditCard.expiration.month);
        var nameValidation = PayEngine.validateCardHolder(this.formData.creditCard.name);
        if (typeof numberValidation !== 'undefined' && typeof numberValidation.error !== 'undefined') {
            this.serverData.errors.creditCard.number = {
                message: mixins.T('cart-payment-card-num-error')
            }
        }
        if (typeof expiryValidation !== 'undefined' && typeof expiryValidation.error !== 'undefined') {
            this.serverData.errors.creditCard.expiration = {
                message: mixins.T('cart-payment-card-expiration-error')
            }
        }
        if (typeof cvcValidation !== 'undefined' && typeof cvcValidation.error !== 'undefined') {
            this.serverData.errors.creditCard.cvc = {
                message: mixins.T('cart-payment-card-code-error')
            }
        }
        if (typeof nameValidation !== 'undefined' && typeof nameValidation.error !== 'undefined') {
            this.serverData.errors.creditCard.name = {
                message: mixins.T('cart-payment-card-owner-error')
            }
        }
    }

    public showErrorsInSection(section: SectionType): void {
        if (!this.state.showErrorSections.includes(section)) {
            this.state.showErrorSections.push(section);
        }
    }

    public shouldShowErrorsInSection(section: SectionType): boolean {
        return this.state.showErrorSections.includes(section);
    }

    public hideAllErrors() {
        this.state.showErrorSections.length = 0;
    }

    async saveVoucher(voucher: string) {
        this.state.serverData.additionalOptions.voucher.code = voucher;
        this.state.serverData.additionalOptions.hasVoucher = true;
        this.removeError('additionalOptions.voucher');
        await this.syncServer(false, SyncServerSource.SaveVoucher);

        if (this.hasError('additionalOptions.voucher')) {
            this.clearVoucherData();
        }
    }

    clearVoucherData() {
        this.state.serverData.additionalOptions.voucher.code = '';
        this.state.serverData.additionalOptions.voucher.price = undefined;
        this.state.serverData.additionalOptions.voucher.priceNumber = undefined;
        this.state.serverData.additionalOptions.voucher.campaignValidUntil = undefined;
        this.state.serverData.additionalOptions.hasVoucher = false;
    }

    removeVoucher() {
        this.clearVoucherData();
        this.removeError('additionalOptions.voucher');
        this.syncServer();
    }

    get hasValidVoucher(): boolean {
        const voucherOptions = this.state.serverData.additionalOptions.voucher;
        return typeof voucherOptions !== 'undefined'
            && !!voucherOptions.priceNumber
            && !!voucherOptions.code;
    }

    removeError(path: string) {
        let splitted = path.split('.');
        let errorKey = splitted.pop();
        if (typeof errorKey !== 'undefined') {
            let errorContainer = splitted.reduce(
                (obj: { [key: string]: any }, index: string) => obj[index] === undefined ? {} : obj[index],
                this.serverData.errors
            );
            delete errorContainer[errorKey];

            let warningContainer = splitted.reduce(
                (obj: { [key: string]: any }, index: string) => obj[index] === undefined ? {} : obj[index],
                this.serverData.warnings
            );
            delete warningContainer[errorKey];

            if (splitted.length > 0
                && (Object.keys(errorContainer).length === 0 || Object.keys(warningContainer).length === 0)) {
                this.removeError(splitted.join('.'));
            }
        }
    }

    hasError(path: string, section: 'errors' | 'warnings' = 'errors', ignoreShowErrors = false) {
        const splitPath = path.split('.');

        if (splitPath[0] === 'invoice') {
            if (!ignoreShowErrors && !this.shouldShowErrorsInSection(SectionType.Payment)) {
                return false;
            }
        }

        if (section === 'errors' && !splitPath.includes('voucher')) {
            if (!ignoreShowErrors && !this.shouldShowErrorsInSection(SectionType.Address)) {
                return false;
            }
        }

        return Boolean(splitPath.reduce(
            (obj: { [key: string]: any }, index: string) => !obj ? undefined : obj[index],
            this.state.serverData[section]
        ));
    }

    getError(path: string, section: 'errors' | 'warnings' = 'errors') {
        const splitPath = path.split('.');
        let error = splitPath.reduce(
            (obj: { [key: string]: any }, index: string) => !obj ? undefined : obj[index],
            this.state.serverData[section]
        );

        if (typeof (error) !== 'undefined') {
            return error;
        }

        return null;
    }

    activateHeaderBarVoucher() {
        if (this.serverData && this.serverData.currentHeaderBar) {
            this.saveVoucher(this.serverData.currentHeaderBar.voucher.code)
        }
    }

    get shouldShowCreditCardInputs(): boolean {
        return this.selectedPayment!.cardDataRequired;
    }

    get selectedPayment(): Option | undefined {
        return Object.values(this.serverPaymentOptions).find((payment) => {
            return payment.id === this.state.serverData.payment;
        });
    }
}

let instance: CheckoutStore;

export default (personalizedCartId: string | null = null) => {
    if (!instance) {
        instance = new CheckoutStore(personalizedCartId);
        // @ts-ignore
        window.cart = instance;
    }

    if (personalizedCartId) {
        instance.personalizedCartId = personalizedCartId;
    }

    return instance;
}
